import { ErrorCode, OpenFeatureEventEmitter, ProviderEvents } from '@openfeature/web-sdk';

import { API_ENDPOINT } from '../constants';

import type { JsonValue, Provider, ResolutionDetails } from '@openfeature/web-sdk';
import type { FlagMap } from './types';

type JsonObjectValue = Exclude<JsonValue, null | boolean | string | number | JsonValue[]>;

// VERIFIED: API_ENDPOINT === `${VITE_API_URL}/api/v1`, so the URL below
// resolves to /api/v1/feature_flags/ (trailing slash matches the router's get("/")).

export class CoreApiWebProvider implements Provider {
  readonly metadata = { name: 'core-api-web-provider' } as const;
  readonly runsOn = 'client' as const;
  readonly events = new OpenFeatureEventEmitter();

  private flags: FlagMap = {};
  private timer: ReturnType<typeof setTimeout> | undefined;
  private closed = false;

  constructor(
    private readonly getToken: () => Promise<string>,
    private readonly pollMs = 60000,
  ) {}

  async initialize(): Promise<void> {
    this.closed = false;
    await this.refresh(true);
    this.scheduleNext();
  }

  // setTimeout-chaining (not setInterval) so a slow fetch can't stack overlapping
  // requests — the next poll is scheduled only after the previous one completes.
  private scheduleNext(): void {
    if (this.closed) return;
    this.timer = setTimeout(() => {
      this.refresh(false)
        .catch(() => {
          // errors are already handled (and emitted) inside refresh's catch block
        })
        .finally(() => this.scheduleNext());
    }, this.pollMs);
  }

  async onContextChange(): Promise<void> {
    await this.refresh(false);
  }

  async onClose(): Promise<void> {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
  }

  private async refresh(initial: boolean): Promise<void> {
    try {
      const token = await this.getToken();
      const res = await fetch(`${API_ENDPOINT}/feature_flags/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`feature flags fetch failed: ${res.status}`);

      const payload: JsonValue = await res.json();
      const next = parseFlagMap(payload);
      const changed = JSON.stringify(next) !== JSON.stringify(this.flags);
      this.flags = next ?? {};
      // SDK already emits ProviderEvents.Ready automatically after initialize() resolves;
      // do NOT emit it manually here — that would double-fire.
      if (!initial && changed) this.events.emit(ProviderEvents.ConfigurationChanged);
    } catch (err) {
      // Fail-to-last-known: keep last-good map (empty on first load -> hook defaults).
      // Must emit Error manually — initialize() never rejects, so SDK's auto-Error won't fire.
      this.events.emit(ProviderEvents.Error, { message: String(err) });
    }
  }

  // The actual @openfeature/web-sdk 1.9 Provider interface requires
  // (flagKey, defaultValue, context, logger) — context and logger are optional
  // here since we don't use them for in-memory resolution.
  resolveBooleanEvaluation(flagKey: string, defaultValue: boolean): ResolutionDetails<boolean> {
    if (Object.prototype.hasOwnProperty.call(this.flags, flagKey)) {
      return { value: this.flags[flagKey], reason: 'CACHED' };
    }
    return { value: defaultValue, reason: 'DEFAULT' };
  }

  resolveStringEvaluation(_k: string, def: string): ResolutionDetails<string> {
    return {
      value: def,
      reason: 'ERROR',
      errorCode: ErrorCode.GENERAL,
      errorMessage: 'boolean-only provider',
    };
  }

  resolveNumberEvaluation(_k: string, def: number): ResolutionDetails<number> {
    return {
      value: def,
      reason: 'ERROR',
      errorCode: ErrorCode.GENERAL,
      errorMessage: 'boolean-only provider',
    };
  }

  resolveObjectEvaluation<T extends JsonValue>(_k: string, def: T): ResolutionDetails<T> {
    return {
      value: def,
      reason: 'ERROR',
      errorCode: ErrorCode.GENERAL,
      errorMessage: 'boolean-only provider',
    };
  }
}

function parseFlagMap(payload: JsonValue): FlagMap {
  if (!isRecord(payload)) return {};

  return Object.fromEntries(
    Object.entries(payload).filter(
      (entry): entry is [string, boolean] => entry[1] === true || entry[1] === false,
    ),
  );
}

function isRecord(value: JsonValue): value is JsonObjectValue {
  return Object.prototype.toString.call(value) === '[object Object]';
}
